import { useMemo, useState } from "react";
import { FAMILY_PAY, TAX_YEAR, gbp } from "@/lib/statutory";

export function PaternityPayCalculator() {
  const [weeklyEarnings, setWeeklyEarnings] = useState(500);
  const [weeksToTake, setWeeksToTake] = useState(2);

  const result = useMemo(() => {
    const rate = Math.min(FAMILY_PAY.standardWeeklyRate, weeklyEarnings * 0.9);
    const weeks = Math.min(weeksToTake, 2);
    return { rate, weeks, total: rate * weeks };
  }, [weeklyEarnings, weeksToTake]);

  return (
    <div className="rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <NumField label="Average weekly earnings (£)" value={weeklyEarnings} onChange={setWeeklyEarnings} min={0} />
        <NumField label="Weeks of paternity leave" value={weeksToTake} onChange={setWeeksToTake} min={1} max={2} />
      </div>

      <div className="mt-8 rounded-2xl bg-brand p-6 text-brand-foreground">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-foreground/60">
          Estimated Statutory Paternity Pay
        </p>
        <p className="mt-3 font-serif text-5xl">{gbp(result.total, { decimals: 2 })}</p>
        <p className="mt-3 text-sm text-brand-foreground/70">
          {result.weeks} week(s) at {gbp(result.rate, { decimals: 2 })}/week ({TAX_YEAR}, lower of 90% earnings or the
          standard rate).
        </p>
      </div>

      <div className="mt-6 rounded-xl border border-border bg-background p-5">
        <h3 className="cursor-pointer text-sm font-medium text-brand">How this is calculated</h3>
        <div className="mt-4 space-y-3 text-sm text-muted-foreground">
          <p>
            Statutory Paternity Pay is up to 2 weeks at the lower of 90% of average weekly earnings or{" "}
            {gbp(FAMILY_PAY.standardWeeklyRate, { decimals: 2 })} ({TAX_YEAR}). Eligible fathers and partners must have
            26 weeks' service by the qualifying week.
          </p>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, min, max }: { label: string; value: number; onChange: (n: number) => void; min?: number; max?: number }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </label>
  );
}
